/**
 * The workspace rules a task form has to obey.
 *
 * Both of these are re-decided in the database — tasks_require_due_date()
 * refuses a client write with no date when the rule is on, and nothing here
 * can grant a date that the trigger would reject. What lives in this file is
 * the part that makes the rule visible BEFORE somebody presses the button,
 * because "task_due_date_required" arriving from Postgres after the fact is
 * not an explanation.
 */

/**
 * The date a new task should start with, or null for none.
 *
 * ── Why the time zone matters here ─────────────────────────────────────────
 *
 * "Due in 7 days" has to be counted from the person's today, not the server's.
 * At 08:00 in Manila it is still yesterday in UTC, so counting in UTC would
 * hand half the team a deadline one day early — every time, invisibly, and
 * only for the people furthest from anyone who could notice. profiles.time_zone
 * exists as of Batch 10; this is the first thing that reads it.
 *
 * Returns an ISO date (YYYY-MM-DD) because that is what <input type="date">
 * and a Postgres `date` column both take. No time component: a due date is a
 * day, and giving it an hour would invent precision nobody chose.
 */
export function defaultDueDate(
  days: number | null | undefined,
  timeZone: string,
  now: Date = new Date(),
): string | null {
  if (days === null || days === undefined || !Number.isFinite(days)) {
    return null;
  }
  const today = localDateParts(now, timeZone);
  if (today === null) {
    return null;
  }
  // Built in UTC and read back in UTC. Date arithmetic on a local-zone Date
  // crosses DST boundaries and can land on the wrong day; a UTC date built
  // from already-localised parts cannot, because it never leaves UTC.
  const shifted = new Date(Date.UTC(today.year, today.month - 1, today.day));
  shifted.setUTCDate(shifted.getUTCDate() + Math.trunc(days));
  return shifted.toISOString().slice(0, 10);
}

/** Today in the given zone, or null if the platform will not honour the zone. */
function localDateParts(
  now: Date,
  timeZone: string,
): { year: number; month: number; day: number } | null {
  try {
    // en-CA formats as YYYY-MM-DD, which is the one locale that needs no
    // parsing of month names or reordering of fields.
    const iso = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);
    const [year, month, day] = iso.split('-').map(Number);
    if (year === undefined || month === undefined || day === undefined) {
      return null;
    }
    return { year, month, day };
  } catch {
    // An unknown zone throws RangeError. Falling back to "no default" is the
    // safe direction: a task with no date is a task somebody can still fix,
    // where a task dated from a zone we guessed at is a wrong date nobody
    // knows to look for.
    return null;
  }
}

/**
 * What is wrong with a task's due date, or null.
 *
 * Only fires when the workspace rule is on. The message names the rule rather
 * than the field, because "Enter a due date" leaves somebody wondering why it
 * was optional yesterday.
 */
export function dueDateProblem(dueDate: string | null, required: boolean): string | null {
  const value = dueDate === null ? '' : dueDate.trim();

  // Shape first, and whether or not the rule is on. `<input type="date">`
  // always hands back YYYY-MM-DD, so in practice this catches a value that
  // arrived some other way — a pasted "08/18/2026", a default computed from a
  // zone the platform did not recognise, a stale draft. Postgres would refuse
  // it as `invalid input syntax for type date`, which is a sentence about a
  // type rather than about the box somebody typed in.
  if (value !== '' && !isIsoDate(value)) {
    return 'That due date is not a date the calendar recognises. Pick one from the picker.';
  }

  if (!required) {
    return null;
  }
  if (value === '') {
    return 'This workspace requires a due date on every task.';
  }
  return null;
}

/** YYYY-MM-DD, and a day that actually exists. */
export function isIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) {
    return false;
  }
  // Round-trip, because Date accepts 2026-02-31 and quietly slides it to
  // March. A date that changes when you read it back is not the date somebody
  // meant, and it would be stored without complaint.
  return parsed.toISOString().slice(0, 10) === value;
}
